import { useState, useEffect, useRef, useMemo } from "react";
import { motion } from "framer-motion";

export default function ClearingMirror({ d }) {
  const prefersReducedMotion = useMemo(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }, []);

  if (!d.clearing_mirror_enabled) return null;

  const bg = d.clearing_mirror_bg || "#050505";
  const accent = d.clearing_mirror_accent || "#D4AF37";

  return prefersReducedMotion
    ? <StaticMirror d={d} bg={bg} accent={accent} />
    : <AnimatedMirror d={d} bg={bg} accent={accent} />;
}

/* ═══ STATIC (reduced motion) ═══ */
function StaticMirror({ d, bg, accent }) {
  return (
    <section className="relative py-32 md:py-40 px-6" style={{ background: bg }} data-testid="section-clearing-mirror">
      <div className="max-w-3xl mx-auto text-center">
        <p className="font-mc-b text-xs tracking-[0.3em] uppercase mb-6" style={{ color: accent }}>The Clearing Mirror</p>
        <h2 className="font-mc-h text-3xl md:text-5xl font-light text-[#F5F5F0] mb-6">{d.clearing_mirror_heading}</h2>
        <div className="max-w-xs mx-auto my-12">
          <img src={d.clearing_mirror_image} alt="Mirror" className="w-full h-auto" />
        </div>
        <p className="font-mc-h text-xl text-zinc-400 font-light italic mb-4">{d.clearing_mirror_text1}</p>
        <p className="font-mc-h text-xl text-zinc-400 font-light italic mb-4">{d.clearing_mirror_text2}</p>
        <p className="font-mc-h text-2xl font-light italic mb-10" style={{ color: accent }}>{d.clearing_mirror_text3}</p>
        <a href={d.clearing_mirror_btn_target || "#packages"} className="inline-block border px-10 py-4 rounded-full font-mc-b tracking-wide uppercase text-sm transition-all duration-300 hover:shadow-[0_0_20px_rgba(212,175,55,0.4)]" style={{ borderColor: accent, color: accent }} data-testid="clearing-mirror-btn">
          {d.clearing_mirror_btn_text}
        </a>
      </div>
    </section>
  );
}

/* ═══ ANIMATED (scroll-driven) ═══ */
function AnimatedMirror({ d, bg, accent }) {
  const sectionRef = useRef(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const onScroll = () => {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const sectionH = el.offsetHeight;
      // progress: 0 when section top hits viewport bottom, 1 when section bottom hits viewport top
      const raw = (vh - rect.top) / (sectionH + vh);
      setProgress(Math.max(0, Math.min(1, raw)));
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Fog clears: blur goes from 20px -> 0 as progress 0.05 -> 0.75
  const fogProgress = Math.max(0, Math.min(1, (progress - 0.05) / 0.7));
  const blurAmount = Math.max(0, 20 * (1 - fogProgress));
  const mirrorOpacity = 0.3 + fogProgress * 0.7;

  // Fog overlay opacity (condensation layer fades out)
  const fogOverlayOpacity = Math.max(0, 1 - fogProgress * 1.4);

  // Text reveals
  const text1Opacity = Math.max(0, Math.min(1, (progress - 0.30) / 0.10));
  const text2Opacity = Math.max(0, Math.min(1, (progress - 0.50) / 0.10));
  const text3Opacity = Math.max(0, Math.min(1, (progress - 0.70) / 0.10));
  const btnOpacity = Math.max(0, Math.min(1, (progress - 0.78) / 0.10));

  // Subtext fades out as mirror clears
  const subtextOpacity = Math.max(0, 1 - progress * 3);

  // Gold glow intensity increases
  const glowIntensity = fogProgress * 0.4;

  return (
    <section
      ref={sectionRef}
      className="relative"
      style={{ background: bg, minHeight: "130vh" }}
      data-testid="section-clearing-mirror"
    >
      {/* Sticky inner container */}
      <div className="sticky top-0 h-screen flex flex-col items-center justify-center overflow-hidden px-6">

        {/* Heading + subtext */}
        <div className="text-center mb-8 md:mb-12 relative z-10">
          <p className="font-mc-b text-xs tracking-[0.3em] uppercase mb-4" style={{ color: accent, opacity: 0.6 }}>The Clearing Mirror</p>
          <h2 className="font-mc-h text-2xl sm:text-3xl md:text-4xl font-light text-[#F5F5F0]">{d.clearing_mirror_heading}</h2>
          <p className="font-mc-b text-sm text-zinc-600 mt-3 transition-opacity duration-300" style={{ opacity: subtextOpacity }}>
            {d.clearing_mirror_subtext}
          </p>
        </div>

        {/* Mirror container */}
        <div className="relative w-[240px] sm:w-[280px] md:w-[320px] lg:w-[360px]">
          {/* Gold glow behind mirror */}
          <div
            className="absolute inset-0 rounded-full pointer-events-none"
            style={{
              background: `radial-gradient(ellipse at center, ${accent}${Math.round(glowIntensity * 255).toString(16).padStart(2, '0')} 0%, transparent 70%)`,
              transform: "scale(1.5)",
              filter: "blur(40px)",
            }}
          />

          {/* Mirror image */}
          <div className="relative">
            <img
              src={d.clearing_mirror_image}
              alt="Mirror"
              className="w-full h-auto relative z-10 select-none"
              style={{
                filter: `blur(${blurAmount}px) brightness(${0.7 + fogProgress * 0.3})`,
                opacity: mirrorOpacity,
                transition: "filter 0.1s linear, opacity 0.1s linear",
              }}
              draggable={false}
            />

            {/* Fog / condensation overlay */}
            <div
              className="absolute inset-0 z-20 pointer-events-none"
              style={{
                opacity: fogOverlayOpacity,
                background: `
                  radial-gradient(ellipse 80% 60% at 50% 40%, rgba(180,180,180,0.35) 0%, transparent 100%),
                  radial-gradient(ellipse 60% 40% at 30% 60%, rgba(200,200,200,0.2) 0%, transparent 100%),
                  radial-gradient(ellipse 50% 50% at 70% 30%, rgba(160,160,160,0.25) 0%, transparent 100%)
                `,
                mixBlendMode: "screen",
                transition: "opacity 0.15s linear",
              }}
            />

            {/* Feathered clearing wipe - center clears first */}
            <div
              className="absolute inset-0 z-20 pointer-events-none"
              style={{
                opacity: fogOverlayOpacity * 0.7,
                background: `radial-gradient(ellipse 100% 100% at 50% 50%, transparent ${fogProgress * 100}%, rgba(120,120,120,0.3) ${fogProgress * 100 + 20}%)`,
                transition: "opacity 0.1s linear",
              }}
            />
          </div>
        </div>

        {/* Revealed text lines */}
        <div className="text-center mt-10 md:mt-14 relative z-10 space-y-4 max-w-2xl">
          <p
            className="font-mc-h text-lg md:text-2xl text-zinc-300 font-light italic"
            style={{ opacity: text1Opacity, transform: `translateY(${(1 - text1Opacity) * 12}px)`, transition: "opacity 0.2s, transform 0.2s" }}
          >
            {d.clearing_mirror_text1}
          </p>
          <p
            className="font-mc-h text-lg md:text-2xl text-zinc-300 font-light italic"
            style={{ opacity: text2Opacity, transform: `translateY(${(1 - text2Opacity) * 12}px)`, transition: "opacity 0.2s, transform 0.2s" }}
          >
            {d.clearing_mirror_text2}
          </p>
          <p
            className="font-mc-h text-xl md:text-3xl font-light italic"
            style={{ color: accent, opacity: text3Opacity, transform: `translateY(${(1 - text3Opacity) * 12}px)`, transition: "opacity 0.2s, transform 0.2s" }}
          >
            {d.clearing_mirror_text3}
          </p>
          <div style={{ opacity: btnOpacity, transform: `translateY(${(1 - btnOpacity) * 16}px)`, transition: "opacity 0.3s, transform 0.3s" }} className="pt-6">
            <a
              href={d.clearing_mirror_btn_target || "#packages"}
              className="inline-block border px-10 py-4 rounded-full font-mc-b tracking-wide uppercase text-sm transition-all duration-300 hover:shadow-[0_0_20px_rgba(212,175,55,0.4)]"
              style={{ borderColor: accent, color: accent, background: btnOpacity > 0.5 ? "transparent" : "transparent" }}
              data-testid="clearing-mirror-btn"
            >
              {d.clearing_mirror_btn_text}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
